import React, { useRef, useState } from "react";
import axios from "axios";
import {
    Dialog,
    Box,
    Typography,
    Button,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";

const PURPLE = "#9C01B9";
const MUTED = "#68717A";

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPT = ".pdf,.jpg,.jpeg,.png";
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];
const ALLOWED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"];

/*
 * ==========================================
 * DOCUMENTOS ACEITOS PELA ROTA /uploadDocumentacao
 * ==========================================
 */

const DOCUMENTOS = [
    {
        tipo: "rgCnh",
        label: "RG ou CNH (Identificação Oficial)",
        hint: "Documento completo (frente e verso no mesmo arquivo)",
        title: "Clique ou arraste o documento de identificação",
        subtitle: "Envie um PDF ou foto contendo frente e verso do documento",
        icon: CameraAltOutlinedIcon,
        obrigatorio: true,
    },
    {
        tipo: "tituloEleitor",
        label: "Título de Eleitor",
        hint: "Opcional se constar no novo documento de identificação",
        title: "Upload do Arquivo do Título (PDF ou Foto)",
        subtitle: "Aceito documento emitido pelo portal do Tribunal Superior Eleitoral (TSE)",
        icon: CloudUploadOutlinedIcon,
        obrigatorio: false,
    },
    {
        tipo: "comprovanteResidencia",
        label: "Comprovante de Residência Atualizado",
        chip: "Máximo 90 dias",
        title: "Clique ou arraste o comprovante recente",
        subtitle: "Contas de consumo válidas: Luz, Água, Gás canalizado, Internet fixa ou Telefone fixo",
        icon: DescriptionOutlinedIcon,
        obrigatorio: true,
    },
];

const estadoInicial = () =>
    DOCUMENTOS.reduce((acc, { tipo }) => ({ ...acc, [tipo]: null }), {});

const validarArquivo = (file) => {
    const extensao = file.name.split(".").pop()?.toLowerCase();

    if (
        !ALLOWED_EXTENSIONS.includes(extensao) ||
        (file.type && !ALLOWED_MIME_TYPES.includes(file.type))
    ) {
        return "Formato inválido. Envie PDF, JPG ou PNG.";
    }

    if (file.size > MAX_FILE_SIZE) {
        return `Arquivo maior que ${MAX_FILE_SIZE_MB}MB.`;
    }

    return null;
};

function UploadBox({
    title,
    subtitle,
    file,
    onSelect,
    onRemove,
    icon,
    error,
    status,
    disabled,
    accept = ACCEPT,
}) {
    const inputRef = useRef(null);
    const hasFile = Boolean(file);

    const handleClick = () => {
        if (disabled) return;
        inputRef.current?.click();
    };

    const handleDrop = (event) => {
        event.preventDefault();

        if (disabled) return;

        const droppedFile = event.dataTransfer.files?.[0];

        if (!droppedFile) return;

        onSelect(droppedFile);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const borderColor = error ? "#D32F2F" : "#E0E3E7";

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                onClick={!hasFile ? handleClick : undefined}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                sx={{
                    width: "100%",
                    minHeight: 105,
                    bgcolor: error ? "#FDF3F3" : "#F4F5F7",
                    borderRadius: 2,
                    border: `1px dashed ${borderColor}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: hasFile || disabled ? "default" : "pointer",
                    transition: "all .2s ease",
                    px: 2,
                    py: 1.5,
                    boxSizing: "border-box",

                    "&:hover": {
                        borderColor: hasFile || disabled ? borderColor : PURPLE,
                        bgcolor: hasFile || disabled
                            ? (error ? "#FDF3F3" : "#F4F5F7")
                            : "#FAF4FB",
                    },
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    hidden
                    onChange={(event) => {
                        const selectedFile = event.target.files?.[0];

                        if (selectedFile) {
                            onSelect(selectedFile);
                        }

                        // Permite selecionar o mesmo arquivo novamente
                        event.target.value = "";
                    }}
                />

                {!hasFile ? (
                    <>
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                bgcolor: "#FFF",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: PURPLE,
                                mb: 0.8,
                            }}
                        >
                            {icon}
                        </Box>

                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#34383C",
                                textAlign: "center",
                            }}
                        >
                            {title}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 9,
                                color: MUTED,
                                mt: 0.4,
                                textAlign: "center",
                            }}
                        >
                            {subtitle}
                        </Typography>
                    </>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            maxWidth: 500,
                        }}
                    >
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                minWidth: 34,
                                borderRadius: 1,
                                bgcolor: "#F4E8F7",
                                color: PURPLE,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <DescriptionOutlinedIcon sx={{ fontSize: 19 }} />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "#303030",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {file.name}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 9,
                                    color: MUTED,
                                    mt: 0.2,
                                }}
                            >
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                                {status === "enviando" && " • Enviando..."}
                            </Typography>
                        </Box>

                        {status === "enviando" ? (
                            <CircularProgress size={17} sx={{ color: PURPLE }} />
                        ) : error ? (
                            <ErrorOutlineIcon sx={{ color: "#D32F2F", fontSize: 19 }} />
                        ) : (
                            <CheckCircleOutlineIcon sx={{ color: "#0A9F5A", fontSize: 19 }} />
                        )}

                        <IconButton
                            size="small"
                            disabled={disabled}
                            onClick={(event) => {
                                event.stopPropagation();
                                onRemove();
                            }}
                            sx={{
                                color: "#777",
                                "&:hover": {
                                    color: "#D32F2F",
                                    bgcolor: "#FDECEC",
                                },
                            }}
                        >
                            <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                )}
            </Box>

            {error && (
                <Typography sx={{ fontSize: 10, color: "#D32F2F", mt: 0.5 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}

function SectionHeader({ numero, label, hint, chip, jaEnviado }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    flexWrap: "wrap",
                }}
            >
                <Box
                    sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        bgcolor: "#F0F1F3",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#62676C",
                        }}
                    >
                        {numero}
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        fontSize: {
                            xs: 13,
                            sm: 14,
                        },
                        fontWeight: 700,
                        color: "#303030",
                    }}
                >
                    {label}
                </Typography>

                {jaEnviado && (
                    <Chip
                        label="Já enviado"
                        size="small"
                        sx={{
                            height: 18,
                            bgcolor: "#DFF7EB",
                            color: "#16834B",
                            fontSize: 8,
                            fontWeight: 600,
                            "& .MuiChip-label": {
                                px: 0.8,
                            },
                        }}
                    />
                )}
            </Box>

            {chip ? (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 18,
                        bgcolor: "#FFE0E0",
                        color: "#D32F2F",
                        fontSize: 8,
                        fontWeight: 600,
                        flexShrink: 0,
                        "& .MuiChip-label": {
                            px: 0.8,
                        },
                    }}
                />
            ) : (
                hint && (
                    <Typography
                        sx={{
                            display: {
                                xs: "none",
                                sm: "block",
                            },
                            fontSize: 9,
                            color: MUTED,
                        }}
                    >
                        {hint}
                    </Typography>
                )
            )}
        </Box>
    );
}

export default function ModalEnviarDocumentos({
    open,
    onClose,
    socio,
    onSuccess,
}) {
    /*
     * ==========================================
     * STATES
     * ==========================================
     */

    const [documentos, setDocumentos] = useState(estadoInicial);
    const [erros, setErros] = useState({});
    const [statusEnvio, setStatusEnvio] = useState({});
    const [erroGeral, setErroGeral] = useState("");
    const [loading, setLoading] = useState(false);
    const [dragging, setDragging] = useState(false);

    // Contador para evitar "piscar" ao passar sobre elementos filhos
    const dragCounter = useRef(0);

    const idFichaCadastral = socio?.idFichaCadastral ?? socio?.id;

    const documentoJaEnviado = (tipo) =>
        Boolean(socio?.documentos?.[`${tipo}FileName`]);

    /*
     * ==========================================
     * FUNÇÕES DE UPLOAD
     * ==========================================
     */

    const handleFileSelect = (file, tipoDocumento) => {
        const erro = validarArquivo(file);

        setErroGeral("");
        setStatusEnvio((prev) => ({ ...prev, [tipoDocumento]: undefined }));

        if (erro) {
            setDocumentos((prev) => ({ ...prev, [tipoDocumento]: null }));
            setErros((prev) => ({ ...prev, [tipoDocumento]: erro }));
            return;
        }

        setDocumentos((prev) => ({ ...prev, [tipoDocumento]: file }));
        setErros((prev) => ({ ...prev, [tipoDocumento]: null }));
    };

    const handleRemoveFile = (tipoDocumento) => {
        setDocumentos((prev) => ({ ...prev, [tipoDocumento]: null }));
        setErros((prev) => ({ ...prev, [tipoDocumento]: null }));
        setStatusEnvio((prev) => ({ ...prev, [tipoDocumento]: undefined }));
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        dragCounter.current += 1;
        setDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        dragCounter.current -= 1;

        if (dragCounter.current <= 0) {
            dragCounter.current = 0;
            setDragging(false);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDropOutside = (event) => {
        // Os UploadBox tratam o arquivo; aqui só reseta o estado visual
        event.preventDefault();
        dragCounter.current = 0;
        setDragging(false);
    };

    /*
     * ==========================================
     * VALIDAÇÃO
     * ==========================================
     */

    const validateFiles = () => {
        const novosErros = {};

        DOCUMENTOS.forEach(({ tipo, obrigatorio }) => {
            const file = documentos[tipo];

            if (file) {
                const erro = validarArquivo(file);
                if (erro) novosErros[tipo] = erro;
            } else if (obrigatorio && !documentoJaEnviado(tipo)) {
                novosErros[tipo] = "Documento obrigatório.";
            }
        });

        setErros(novosErros);

        if (Object.keys(novosErros).length > 0) {
            setErroGeral("Verifique os documentos destacados antes de enviar.");
            return false;
        }

        const selecionados = DOCUMENTOS.filter(({ tipo }) => documentos[tipo]);

        if (selecionados.length === 0) {
            setErroGeral("Selecione pelo menos um documento para enviar.");
            return false;
        }

        if (!idFichaCadastral) {
            setErroGeral("Não foi possível identificar o sócio. Recarregue a página e tente novamente.");
            return false;
        }

        setErroGeral("");
        return true;
    };

    /*
     * ==========================================
     * ENVIO
     * ==========================================
     */

    const enviarDocumento = (tipoDocumento, file) => {
        const formData = new FormData();

        // Campos de texto antes do arquivo para que o multer já os tenha em req.body
        formData.append("tipoDocumento", tipoDocumento);
        formData.append("idFichaCadastral", idFichaCadastral);
        formData.append("file", file);

        return axios.post(
            `${process.env.REACT_APP_API_URL}/uploadDocumentacao`,
            formData,
            {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            }
        );
    };

    const handleSubmit = async () => {
        if (loading || !validateFiles()) return;

        setLoading(true);

        const pendentes = DOCUMENTOS.filter(({ tipo }) => documentos[tipo]);
        const falhas = {};

        // A rota recebe um arquivo por vez (upload.single), então envia em sequência
        for (const { tipo } of pendentes) {
            setStatusEnvio((prev) => ({ ...prev, [tipo]: "enviando" }));

            try {
                await enviarDocumento(tipo, documentos[tipo]);

                setStatusEnvio((prev) => ({ ...prev, [tipo]: "enviado" }));
                setDocumentos((prev) => ({ ...prev, [tipo]: null }));
            } catch (error) {
                console.error(`Erro ao enviar ${tipo}:`, error);

                falhas[tipo] =
                    error?.response?.data?.mensagem ||
                    "Não foi possível enviar o documento.";

                setStatusEnvio((prev) => ({ ...prev, [tipo]: "erro" }));
            }
        }

        setErros(falhas);
        setLoading(false);

        const enviados = pendentes.length - Object.keys(falhas).length;

        if (enviados > 0) {
            onSuccess?.();
        }

        if (Object.keys(falhas).length > 0) {
            setErroGeral(
                enviados > 0
                    ? "Alguns documentos foram enviados, mas outros falharam. Tente reenviar os destacados."
                    : "Não foi possível enviar os documentos. Tente novamente."
            );
            return;
        }

        resetar();
        onClose?.();
    };

    /*
     * ==========================================
     * FECHAR MODAL
     * ==========================================
     */

    const resetar = () => {
        setDocumentos(estadoInicial());
        setErros({});
        setStatusEnvio({});
        setErroGeral("");
        setDragging(false);
        dragCounter.current = 0;
    };

    const handleClose = () => {
        if (loading) return;

        resetar();
        onClose?.();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: "hidden",
                    m: {
                        xs: 1,
                        sm: 2,
                    },
                    width: {
                        xs: "calc(100% - 16px)",
                        sm: "100%",
                    },
                    maxHeight: "calc(100vh - 32px)",
                },
            }}
        >
            <Box
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDropOutside}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    bgcolor: "#FFF",
                    minHeight: 0,
                    border: dragging
                        ? `2px dashed ${PURPLE}`
                        : "2px solid transparent",
                    transition: "border .2s ease",
                }}
            >
                {/* =========================================
                    HEADER
                ========================================= */}

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                        px: {
                            xs: 2,
                            sm: 2.5,
                        },
                        pt: 2,
                        pb: 1.5,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                                flexWrap: "wrap",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: 15,
                                        sm: 16,
                                    },
                                    fontWeight: 700,
                                    color: "#202020",
                                }}
                            >
                                Enviar Documentos
                            </Typography>

                            {socio?.nome && (
                                <>
                                    <Typography
                                        sx={{
                                            fontSize: 15,
                                            color: "#6F6F6F",
                                        }}
                                    >
                                        —
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: "#303030",
                                        }}
                                    >
                                        {socio.nome}
                                    </Typography>

                                    <Chip
                                        label={
                                            socio?.tipoSocio ||
                                            "Sócio"
                                        }
                                        size="small"
                                        sx={{
                                            height: 18,
                                            bgcolor: "#F4DDF8",
                                            color: PURPLE,
                                            fontSize: 10,
                                            fontWeight: 600,
                                            "& .MuiChip-label": {
                                                px: 0.8,
                                            },
                                        }}
                                    />
                                </>
                            )}
                        </Box>

                        <Typography
                            sx={{
                                fontSize: {
                                    xs: 10,
                                    sm: 11,
                                },
                                color: MUTED,
                                mt: 0.4,
                                maxWidth: 650,
                                lineHeight: 1.4,
                            }}
                        >
                            Formatos suportados: PDF, JPG, PNG (máx. {MAX_FILE_SIZE_MB}MB
                            por arquivo). Certifique-se de que os dados
                            estejam legíveis.
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={handleClose}
                        disabled={loading}
                        size="small"
                        sx={{
                            color: "#555",
                            mt: -0.5,
                            mr: -0.5,
                            flexShrink: 0,
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                </Box>

                {/* =========================================
                    DOCUMENTOS
                ========================================= */}

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.8,
                        px: {
                            xs: 2,
                            sm: 2.5,
                        },
                        pb: 2,
                        overflowY: "auto",
                    }}
                >
                    {erroGeral && (
                        <Alert
                            severity="error"
                            onClose={() => setErroGeral("")}
                            sx={{ fontSize: 11, py: 0 }}
                        >
                            {erroGeral}
                        </Alert>
                    )}

                    {DOCUMENTOS.map((doc, index) => {
                        const Icon = doc.icon;

                        return (
                            <Box
                                key={doc.tipo}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.8,
                                }}
                            >
                                <SectionHeader
                                    numero={index + 1}
                                    label={doc.label}
                                    hint={doc.hint}
                                    chip={doc.chip}
                                    jaEnviado={
                                        documentoJaEnviado(doc.tipo) ||
                                        statusEnvio[doc.tipo] === "enviado"
                                    }
                                />

                                <UploadBox
                                    icon={<Icon sx={{ fontSize: 19 }} />}
                                    title={doc.title}
                                    subtitle={
                                        documentoJaEnviado(doc.tipo)
                                            ? "Documento já enviado. Selecione um novo arquivo para substituí-lo."
                                            : doc.subtitle
                                    }
                                    file={documentos[doc.tipo]}
                                    error={erros[doc.tipo]}
                                    status={statusEnvio[doc.tipo]}
                                    disabled={loading}
                                    onSelect={(file) =>
                                        handleFileSelect(file, doc.tipo)
                                    }
                                    onRemove={() =>
                                        handleRemoveFile(doc.tipo)
                                    }
                                />
                            </Box>
                        );
                    })}
                </Box>

                {/* =========================================
                    FOOTER
                ========================================= */}

                <Box
                    sx={{
                        bgcolor: "#F5F6F8",
                        borderTop: "1px solid #ECEDEF",
                        px: {
                            xs: 2,
                            sm: 2.5,
                        },
                        py: 1.4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexDirection: {
                            xs: "column-reverse",
                            sm: "row",
                        },
                    }}
                >
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            alignSelf: {
                                xs: "stretch",
                                sm: "auto",
                            },
                            color: "#454545",
                            textTransform: "none",
                            fontSize: 12,
                            fontWeight: 500,
                            justifyContent: {
                                xs: "center",
                                sm: "flex-start",
                            },
                        }}
                    >
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        startIcon={
                            loading ? (
                                <CircularProgress size={14} sx={{ color: "#FFF" }} />
                            ) : (
                                <SendOutlinedIcon
                                    sx={{
                                        fontSize:
                                            "16px !important",
                                    }}
                                />
                            )
                        }
                        sx={{
                            width: {
                                xs: "100%",
                                sm: "auto",
                            },
                            minWidth: {
                                sm: 210,
                            },
                            height: 38,
                            px: 2.5,
                            bgcolor: PURPLE,
                            borderRadius: 1.5,
                            textTransform: "none",
                            fontSize: 11,
                            fontWeight: 700,
                            boxShadow: "0 3px 10px rgba(156, 1, 185, .25)",

                            "&:hover": {
                                bgcolor: "#82009A",
                                boxShadow:
                                    "0 4px 12px rgba(156, 1, 185, .3)",
                            },

                            "&.Mui-disabled": {
                                bgcolor: "#C77FD6",
                                color: "#FFF",
                            },
                        }}
                    >
                        {loading ? "Enviando..." : "Salvar e Enviar para Validação"}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
